import { useMemo } from 'react';
import { isNil, omitBy } from 'lodash';

import { ensureDefined } from '@e-socna/utils';
import { EMPTY_STRING } from '@e-socna/empty';
import { getFragmentData } from '@e-socna/data-layer';
import type { FragmentType } from '@e-socna/data-layer';

import { useIdFactory } from '../../../hooks/useIdFactory.hook';
import { useReferencesContext } from '../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';

import { QuotationHeaderForm_QuotationHeader_Fragment } from '../fragments/quotation-header.fragment';
import type { QuotationHeaderForm_QuotationHeader } from '../fragments/quotation-header.fragment';

export const useInitialValuesFactory = (
  ppInitialValues?: Readonly<
    FragmentType<typeof QuotationHeaderForm_QuotationHeader_Fragment>
  >,
) => {
  const pInitialValues = getFragmentData(
    QuotationHeaderForm_QuotationHeader_Fragment,
    ppInitialValues,
  );

  const { generateId } = useIdFactory();

  const referencesContext = useReferencesContext();

  const { defaultPaymentMethod, defaultPaymentTerm, defaultValidityPeriod } =
    ensureDefined({
      defaultPaymentMethod: referencesContext.defaultPaymentMethod,
      defaultPaymentTerm: referencesContext.defaultPaymentTerm,
      defaultValidityPeriod: referencesContext.defaultValidityPeriod,
    });

  const initialValues: QuotationHeaderForm_QuotationHeader = useMemo(
    () => ({
      __typename: 'QuotationHeader' as const,
      _id: generateId(),
      interventionDate: EMPTY_STRING,
      modalities: EMPTY_STRING,
      note: EMPTY_STRING,
      paymentMethodId: defaultPaymentMethod._id,
      paymentTermId: defaultPaymentTerm._id,
      validityPeriodId: defaultValidityPeriod._id,
      ...omitBy(pInitialValues, isNil),
    }),
    [
      defaultPaymentMethod._id,
      defaultPaymentTerm._id,
      defaultValidityPeriod._id,
      generateId,
      pInitialValues,
    ],
  );

  return { initialValues };
};
